import {$qs} from '../../../../../@shared/ts/dom';
import {type StripeGateway, type IStripeContext} from '../../../models/stripe';
import {PeachPayCustomer} from '../../../reducers/peachPayCustomerReducer';
import {getLocaleText} from '../../../util/translation';
import {createSetupIntent} from '../setup-intent';
import bankImageURL from '../../../../img/marks/bank.svg';
import {Feature} from '../../../reducers/environmentReducer';
import {FeatureFlag} from '../../../../../@type/features';

// https://stripe.com/docs/payments/ach-debit/accept-a-payment
function initStripeACHDebitMethod(): StripeGateway {
	return {
		config: {
			name: Feature.metadata<string>(FeatureFlag.STRIPE_ACHDEBIT_GATEWAY, 'title') ?? getLocaleText('US bank account'),
			description: /* html */ `<p style="margin: 0;">${getLocaleText('After selecting <b>Pay</b> a prompt will appear to select a bank account.')}</p><hr><p class="muted" style="margin: 0;">${
				getLocaleText('Note: Payments may take up to 5 days to complete.')
			}</p>`,
			gatewayId: 'peachpay_stripe_achdebit',
			assets: {
				title: {src: bankImageURL},
				badge: {src: bankImageURL},
			},
		},
		async tokenize(context: IStripeContext) {
			const {stripe} = context;

			const clientSecret = await createSetupIntent('us_bank_account');
			if (!clientSecret) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to create setup intent.',
				}];
			}

			const {error: collectError, setupIntent: collectIntent} = await stripe.collectBankAccountForSetup({
				clientSecret,
				params: {
					payment_method_type: 'us_bank_account',
					payment_method_data: {
						billing_details: {
							email: PeachPayCustomer.billing.email(),
							name: PeachPayCustomer.billing.firstName() + ' ' + PeachPayCustomer.billing.lastName(),
						},
					},
				},
			});
			if (!collectIntent || collectError) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: collectError?.message ?? 'Failed to collect bank account for setup intent.',
				}];
			}

			if (collectIntent.status !== 'requires_confirmation') {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'The customer canceled choosing a bank account.',
				}];
			}

			const mandateResponse = await displayMandate({
				fullName: PeachPayCustomer.billing.fullName(),
				clientName: window.top?.location.hostname ?? '',
			});
			if (!mandateResponse) {
				const message = getLocaleText('The required mandate was not accepted. Payment canceled.');
				return [null, {note: message}];
			}

			const {error: confirmError, setupIntent: confirmIntent} = await stripe.confirmUsBankAccountSetup(clientSecret);
			if (!confirmIntent || confirmError) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: confirmError?.message ?? 'Failed to confirm setup intent.',
				}];
			}

			if (confirmIntent.status !== 'succeeded') {
				return [null, {
					paymentStatus: 'creationfailed',
					note: `Setup intent resulted in invalid status(${confirmIntent.status})`,
				}];
			}

			return [confirmIntent.payment_method as string, {}];
		},
		async confirm(context: IStripeContext, clientSecret: string) {
			const {stripe} = context;

			const {paymentIntent, error} = await stripe.retrievePaymentIntent(clientSecret);

			if (!paymentIntent || error) {
				const message = error?.message ?? 'Failed to retrieve payment intent.';
				return [null, {
					note: message,
				}];
			}

			if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing') {
				return ['success', {}];
			}

			return [null, {
				note: `Failed to confirm payment intent with status(${paymentIntent.status})`,
			}];
		},
	};
}

async function displayMandate(options: IMandateOptions): Promise<boolean> {
	return new Promise((res, rej) => {
		$qs('body', $el => {
			$el.insertAdjacentHTML('beforeend', mandateTemplate(options));
		});

		const $container = $qs<HTMLDivElement>('#ach-mandate-container');
		const $acceptButton = $qs<HTMLButtonElement>('#ach-button-accept');
		const $cancelButton = $qs<HTMLButtonElement>('#ach-button-cancel');
		const $xButton = $qs<HTMLButtonElement>('#ach-button-x');

		if (!$container || !$acceptButton || !$cancelButton || !$xButton) {
			$container?.remove();
			rej(new Error('Developer error: Mandate was not mounted.'));
			return;
		}

		$acceptButton.addEventListener('click', () => {
			$container.remove();
			res(true);
		});

		$cancelButton.addEventListener('click', () => {
			$container.remove();
			res(false);
		});

		$xButton.addEventListener('click', () => {
			$container.remove();
			res(false);
		});
	});
}

interface IMandateOptions {
	fullName: string;
	clientName: string;
}

function mandateTemplate(options: IMandateOptions): string {
	const date = new Date().toLocaleDateString();
	const nameHtml = options.fullName === ' ' ? '' : `<p>Name: ${options.fullName}</p>`;

	const template = /* html */ `
<div id="ach-mandate-container">
    <div id="ach-mandate-modal">
        <div id="ach-mandate-header">
            <button type="button" id="ach-button-x" class="txt-btn" style="font-size: 2rem;">×</button>
        </div>
        <div id="ach-mandate-body">
            <span>
                ${nameHtml}
                <p>Date: ${date}</p>
            </span>
            <br>
            <p>
                <!-- Note this text is only applicable to non recurring orders! -->
                ${getLocaleText('By clicking <b>Accept</b>, you authorize')} <span class="client">${options.clientName}</span> 
                ${getLocaleText('to debit the bank account specified above for any amount owed for charges arising from your use of')}
                <span class="client">${options.clientName}'s</span> ${getLocaleText('services and/or purchase of products from ')}
                <span class="client">${options.clientName}</span>, ${getLocaleText('pursuant to')} <span class="client">${options.clientName}'s</span> ${
	getLocaleText('website and terms, until this authorization is revoked. You may amend or cancel this authorization at any time by providing notice to ')
}
                <span class="client">${options.clientName}</span> ${getLocaleText('with 30 (thirty) days notice.')}
            </p>
            <br>
            <div style="text-align: center;">
                <button type="button" id="ach-button-accept" class="btn" style="padding: 9px; width: 7rem;">${getLocaleText('Accept')}</button>
                <button type="button" id="ach-button-cancel" class="txt-btn">${getLocaleText('Cancel')}</button>
            </div>
        </div>
    </div>
    <style>
        #ach-mandate-container{
            position: fixed;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            background-color: rgba(0,0,0,0.5);
            animation-name: fadein;
            animation-duration: 0.4s;
        }

        @keyframes fadein{
            from {
                background-color: rgba(0,0,0,0);
            }
            to {
                background-color: rgba(0,0,0,0.5);
            }
        }

        #ach-mandate-container .client{
            border: 1px solid #e6e6e6;
            border-radius: 0.2rem;
            background-color: #e6e6e6;
            padding: 0 4px;
            margin-bottom: 1px;
            display: inline-block;
        }

        #ach-mandate-modal{
            position: absolute;
            background-color: white;
            border-radius: 0.4rem;
            z-index: 10000000000;
            width: 50vw;
            box-shadow:  2px 8px 20px 3px rgb(0 0 0 / 20%);
            margin: 0 calc(49vw / 2);
            top: 10vh;
        }

        #ach-mandate-header{
            padding: 0 0.5rem;
            text-align: right;
            border-bottom: 1px solid #e6e6e6;
        }

        #ach-mandate-body{
            padding: 1.5rem;
        }

        #ach-mandate-body p {
            margin: 0;
            padding: 0;
        }
        
        #ach-mandate-modal .txt-btn{
            border: none;
            background: none;
            color: grey;
            font-weight: 400;
        }

        #ach-mandate-modal .txt-btn:hover{
            cursor: pointer;
            color: red;
        } 

        @media screen and (max-width: 768px) {

            
            #ach-mandate-modal{
                width: 100%;
                margin: 0;
                left: 0;
                right: 0;

                animation-duration: 0.8s;
                animation-name: slideup;
                top: unset;
                bottom: 0;
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
            }

            @keyframes slideup{
                from {
                    bottom: -100%
                }
                to {
                    bottom: 0;
                }
            }

        }

        /*This prevents the background from being scrollable*/
        body{
            overflow: hidden !important;
            touch-action: none !important;
        }
    </style>
</div>`;

	return template;
}

export {initStripeACHDebitMethod};
